import { useQuery } from '@tanstack/react-query'
import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { ArrowLeftRight, Settings2 } from 'lucide-react'
import { useState } from 'react'

import { tillListQueryOptions, vaultsQueryOptions } from '@/api/teller-api'
import type { Till, TillStatus, TillType } from '@/api/teller-types'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { ErrorSurface } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { CashMovementDialog } from '@/features/teller/cash-movement-dialog'
import { TILL_STATUSES, TILL_TYPES, tillTypeLabel } from '@/features/teller/constants'
import { TELLER_PATHS, useTellerNavigate, useTellerRawSearch } from '@/features/teller/navigation'
import { TELLER_PERMISSIONS, useTellerPermissions } from '@/features/teller/permissions'
import { PermissionNotice, VaultPanel } from '@/features/teller/teller-panels'
import { TellerLink } from '@/features/teller/teller-link'
import { usePostingDay } from '@/features/teller/use-posting-day'
import { formatBusinessDate } from '@/lib/datetime'
import { useIsAtLeast } from '@/lib/use-viewport'

import { DEFAULT_PAGE_SIZE, tellerCashPositionSearchSchema } from './search'

/**
 * Where the branch's cash is right now: the vault, and every drawer hanging off it.
 *
 * This one reads on a phone. It is a list, and a list is not the thing touch is bad at — the policy
 * line is drawn at keying an amount, so the movement dialog is the part that asks for a wider
 * window, not the table it is launched from.
 *
 * Two honest gaps are stated rather than papered over. Vault balances need `teller.vault.view`,
 * which the shipped Teller role does not hold — the panel says so instead of showing a branch with
 * no vault. And till balances need `teller.till.view`, which no field role holds at all: the loan
 * officer actually carrying a field drawer cannot see this screen, so it answers "where is our cash"
 * for an audience that excludes the people carrying some of it.
 */

/** Nothing here is sortable: the list endpoint takes no ordering parameter. */
const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

const helper = dataTableColumns<Till>()

const columns: DataTableColumn<Till>[] = helper.columns([
  helper.accessor('tillNumber', {
    header: 'Till',
    enableSorting: false,
    cell: (context) => <span className="font-mono text-xs">{context.row.original.tillNumber}</span>,
    meta: { noWrap: true, widthClass: 'w-28', card: 'lead' },
  }),
  helper.accessor('name', {
    header: 'Name',
    enableSorting: false,
    cell: (context) => (
      <span className="font-medium text-heading">{context.row.original.name}</span>
    ),
    meta: { card: 'title' },
  }),
  helper.accessor('tillType', {
    header: 'Type',
    enableSorting: false,
    cell: (context) => tillTypeLabel(context.row.original.tillType),
    meta: { noWrap: true, widthClass: 'w-32' },
  }),
  helper.accessor('cashOnHandMinor', {
    header: 'Cash on hand',
    enableSorting: false,
    cell: (context) => (
      <MoneyDisplay
        value={context.row.original.cashOnHandMinor}
        currency={context.row.original.currencyCode}
        emphasis="strong"
      />
    ),
    meta: { align: 'right', widthClass: 'w-40', card: 'lead' },
  }),
  helper.accessor('cashHoldingLimitMinor', {
    header: 'Limit',
    enableSorting: false,
    // Null is a drawer with no limit, and it renders as a dash rather than as zero — a zero limit
    // would read as "this drawer may hold nothing", which is the opposite of what null means.
    cell: (context) => (
      <MoneyDisplay
        value={context.row.original.cashHoldingLimitMinor}
        currency={context.row.original.currencyCode}
        emphasis="muted"
      />
    ),
    meta: { align: 'right', widthClass: 'w-40' },
  }),
  helper.display({
    id: 'headroom',
    header: 'Headroom',
    cell: (context) => {
      const till = context.row.original
      const headroom =
        till.cashHoldingLimitMinor === null ? null : till.cashHoldingLimitMinor - till.cashOnHandMinor

      return (
        <MoneyDisplay value={headroom} currency={till.currencyCode} colourNegative signed />
      )
    },
    meta: { align: 'right', widthClass: 'w-40' },
  }),
  helper.accessor('status', {
    header: 'Status',
    enableSorting: false,
    cell: (context) => <StatusBadge status={context.row.original.status} />,
    meta: { widthClass: 'w-28' },
  }),
  helper.display({
    id: 'assignment',
    header: 'Held by',
    cell: (context) => {
      const till = context.row.original
      if (till.tillType !== 'Field') {
        return <span className="text-muted">—</span>
      }

      return till.assignedUserId === null ? (
        <span className="text-xs text-muted">Unassigned</span>
      ) : (
        <span className="text-xs">
          <span className="font-mono">{till.assignedUserId.slice(0, 8)}</span>
          {till.assignedForDate ? (
            <span className="block text-muted">{formatBusinessDate(till.assignedForDate)}</span>
          ) : null}
        </span>
      )
    },
    meta: { widthClass: 'w-40' },
  }),
])

export function TellerCashPositionPage() {
  const navigate = useTellerNavigate()
  const rawSearch = useTellerRawSearch()
  const permissions = useTellerPermissions()
  const day = usePostingDay()
  const isWide = useIsAtLeast('lg')

  const [moving, setMoving] = useState(false)

  const search = tellerCashPositionSearchSchema.parse(rawSearch)
  const page = search.page ?? 1
  const pageSize = search.pageSize ?? DEFAULT_PAGE_SIZE

  const vaults = useQuery({
    ...vaultsQueryOptions(day.branchId),
    enabled: permissions.canViewVaults && Boolean(day.branchId),
  })

  const tills = useQuery({
    ...tillListQueryOptions({
      branchId: day.branchId ?? undefined,
      tillType: search.tillType,
      status: search.status,
      page,
      pageSize,
    }),
    enabled: permissions.canViewTills,
  })

  const updateSearch = (patch: Partial<typeof search>) => {
    navigate(TELLER_PATHS.cashPosition, {
      search: {
        tillType: search.tillType,
        status: search.status,
        page: search.page,
        pageSize: search.pageSize,
        ...patch,
      },
    })
  }

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const current: PaginationState = { pageIndex: page - 1, pageSize }
    const next = typeof updater === 'function' ? updater(current) : updater
    updateSearch({ page: next.pageIndex + 1, pageSize: next.pageSize })
  }

  return (
    <>
      <PageHeader
        title="Cash position"
        description="Vault and drawers, as they stand now. Every float and every banking passes between the two."
        actions={
          <>
            {permissions.canManageInfrastructure ? (
              <Button asChild variant="secondary">
                <TellerLink to={TELLER_PATHS.setup}>
                  <Settings2 />
                  Tills and vaults
                </TellerLink>
              </Button>
            ) : null}
            {permissions.canMoveVaultCash ? (
              <Button
                variant="primary"
                onClick={() => setMoving(true)}
                disabled={!isWide || !day.branchId}
                title={isWide ? undefined : 'Moving cash asks for a wider display.'}
              >
                <ArrowLeftRight />
                Move cash
              </Button>
            ) : null}
          </>
        }
      />

      {!isWide && permissions.canMoveVaultCash ? (
        <p className="text-xs text-muted">
          Reading the position works at this width. Moving cash does not: the amount is keyed, and
          that is the one thing this application asks for a wider display to do.
        </p>
      ) : null}

      {permissions.canViewVaults ? (
        vaults.isPending ? (
          <Skeleton className="h-32 w-full" />
        ) : vaults.isError ? (
          <ErrorSurface
            error={vaults.error}
            onRetry={() => void vaults.refetch()}
            title="The vaults could not be read"
          />
        ) : (
          <VaultPanel vaults={vaults.data} />
        )
      ) : (
        <PermissionNotice permission={TELLER_PERMISSIONS.vaultView} what="Vault cash" />
      )}

      {!permissions.canViewTills ? (
        <PermissionNotice permission={TELLER_PERMISSIONS.tillView} what="Till cash" />
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-44 space-y-1.5">
              <Label htmlFor="till-type-filter">Type</Label>
              <Select
                value={search.tillType ?? 'all'}
                onValueChange={(value) =>
                  updateSearch({
                    tillType: value === 'all' ? undefined : (value as TillType),
                    page: undefined,
                  })
                }
              >
                <SelectTrigger id="till-type-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Every drawer</SelectItem>
                  {TILL_TYPES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {tillTypeLabel(value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-44 space-y-1.5">
              <Label htmlFor="till-status-filter">Status</Label>
              <Select
                value={search.status ?? 'all'}
                onValueChange={(value) =>
                  updateSearch({
                    status: value === 'all' ? undefined : (value as TillStatus),
                    page: undefined,
                  })
                }
              >
                <SelectTrigger id="till-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any status</SelectItem>
                  {TILL_STATUSES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={tills.data?.items}
            rowCount={tills.data?.totalCount}
            pagination={{ pageIndex: page - 1, pageSize }}
            onPaginationChange={onPaginationChange}
            sorting={UNSORTED}
            onSortingChange={ignoreSortingChange}
            isLoading={tills.isPending}
            isFetching={tills.isFetching}
            error={tills.isError ? tills.error : null}
            onRetry={() => void tills.refetch()}
            getRowId={(row) => row.id}
            onRowActivate={(till) => navigate(TELLER_PATHS.till(till.id))}
            pageSizeOptions={[DEFAULT_PAGE_SIZE, 50, 100]}
            emptyTitle="No drawers"
            emptyDescription="This branch has no till matching the filters. A till is created once, as branch infrastructure, under admin.branch.manage."
          />
        </div>
      )}

      {day.branchId ? (
        <CashMovementDialog
          branchId={day.branchId}
          vaults={vaults.data ?? []}
          tills={tills.data?.items ?? []}
          open={moving}
          onOpenChange={setMoving}
        />
      ) : null}
    </>
  )
}
